import { isPlatformBrowser } from '@angular/common';
import { Component, ChangeDetectionStrategy, OnInit, PLATFORM_ID, HostListener, ElementRef, ViewChild, Input, ApplicationRef, ChangeDetectorRef, OnDestroy, inject, DestroyRef } from '@angular/core';
import { Subject, debounceTime, filter, first, interval, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GalleryIntl } from './gallery.intl';
import { IconComponent } from '@knorr-bremse-portals/ngx-components/components/icon';
import { iconArrowLeft, iconArrowRight } from '@knorr-bremse-portals/icons';

@Component({
  selector: 'kb-gallery',
  templateUrl: './gallery.component.html',
  styleUrls: ['./gallery.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ IconComponent ],
})
export class GalleryComponent implements OnInit, OnDestroy {

  private static readonly pageSwitchTime = 5000; // TODO: move the time to a central place

  protected iconArrowLeft = iconArrowLeft;
  protected iconArrowRight = iconArrowRight;

  @Input()
  private automaticNavigation = false;

  @ViewChild('pagesElement', { static: true }) private pagesElement!: ElementRef<HTMLUListElement>;

  private resize$ = new Subject<void>();
  private resizeObserver?: ResizeObserver;
  protected hasPages = false;
  private isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private destroyRef = inject(DestroyRef);

  private appRef = inject(ApplicationRef);
  private cdr = inject(ChangeDetectorRef);
  protected intl = inject(GalleryIntl);

  public constructor() {
    this.intl.changes$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.cdr.markForCheck());
  }

  public ngOnInit(): void {
    if (this.automaticNavigation) {
      this.initAutomaticNavigation();
    }

    if (this.isPlatformBrowser) {
      this.resizeObserver = new ResizeObserver(() => {
        this.resize$.next();
      });
      this.resizeObserver.observe(this.pagesElement.nativeElement);

      this.resize$.pipe(
        debounceTime(100),
      ).subscribe(() => {
        const pagesContainer = this.pagesElement.nativeElement;
        const scrollViewportWidth = pagesContainer.offsetWidth;
        const scrollWidth = pagesContainer.scrollWidth;
        const pages = (scrollWidth / scrollViewportWidth) - 1;
        const hasPages = pages > 0;
        if (hasPages !== this.hasPages) {
          this.hasPages = hasPages;
          this.cdr.markForCheck();
        }
      });
    }
  }

  public ngOnDestroy(): void {
    this.resize$.complete();
    this.resizeObserver?.disconnect();
  }

  private hover = false;
  @HostListener('mouseenter')
  private enableHover(): void {
    this.hover = true;
  }
  @HostListener('mouseleave')
  private disableHover(): void {
    this.hover = false;
  }

  private initAutomaticNavigation() {
    if (!this.isPlatformBrowser) {
      return;
    }

    this.appRef.isStable.pipe(
      first(stable => stable),
      switchMap(() => interval(GalleryComponent.pageSwitchTime)),
      filter(() => !this.hover),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.openNextPage();
    });
  }

  private navigatePage(next: boolean) {
    const pagesContainer = this.pagesElement.nativeElement;
    const scrollViewportWidth = pagesContainer.offsetWidth;
    const scrollWidth = pagesContainer.scrollWidth;
    const scrollLeft = Math.ceil(pagesContainer.scrollLeft); // This is because this returns strange float numbers
    const pages = (scrollWidth / scrollViewportWidth) - 1;
    const activePage = scrollLeft / scrollViewportWidth;

    let targetPage = activePage + (next ? 1 : -1);
    if (targetPage > pages && scrollLeft >= (scrollWidth - scrollViewportWidth)) {
      targetPage = 0;
    } else if (targetPage < 0 && activePage <= 0) {
      targetPage = pages;
    }

    const targetScrollLeft = Math.ceil(targetPage * scrollViewportWidth);

    pagesContainer.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });
  }

  protected openPreviousPage() {
    this.navigatePage(false);
  }

  protected openNextPage() {
    this.navigatePage(true);
  }

}
